// Tenancy foundation: customers (tenants) above networks, departments, role + scope assignments
// and the rental radio inventory (documentation/protocol.md "Tenants, departments and rental
// inventory"). The server enforces authorization; nothing here is a security boundary.

export interface NetworkSummary {
  id: string;
  name: string;
  radio_count: number;
  group_count: number;
}

export interface Tenant {
  id: string;
  name: string;
  created_at: string;
  networks: NetworkSummary[];
  department_count: number;
  user_count: number;
  // Platform administrators only: the customer's OEM Partner (null = direct RadioOS customer).
  oem_partner?: { id: string; name: string; status: 'active' | 'disabled' } | null;
  direct?: boolean;
}

export interface Department {
  id: string;
  tenant_id: string;
  parent_department_id: string | null;
  name: string;
  description: string;
  radio_count: number;
  group_count: number;
  member_count: number;
}

export interface ScopedResource {
  id: string;
  label: string;
  name: string;
  network_id: string;
  network_name: string;
}

export interface DepartmentDetail extends Department {
  radios: ScopedResource[];
  groups: ScopedResource[];
  members: { assignment_id: string; user_id: string; username: string; role: string }[];
}

export interface TenantUser {
  id: string;
  username: string;
  legacy_role: string;
  network_id: string;
}

export interface RoleAssignment {
  id: string;
  user_id: string;
  username: string;
  role: string;
  tenant_id: string | null;
  department_id: string | null;
  department_name: string | null;
}

export type InventoryState = 'AVAILABLE' | 'ASSIGNED' | 'PENDING_APPROVAL' | 'REJECTED';

export interface InventoryRadio {
  id: string;
  radio_id: string;
  name: string;
  model: string;
  serial_number?: string;
  status: string;
  state: InventoryState;
  network_id?: string;
  network_name?: string;
  tenant_id?: string;
  tenant_name?: string;
  assigned_at?: string;
  reference?: string;
  department_ids: string[];
}

export interface InventoryResult {
  changed: string[];
  unchanged: string[];
  network?: { id: string; name: string; tenant_id: string; tenant_name: string };
}

export interface AssignmentRecord {
  id: string;
  radio_id: string | null;
  radio_label: string;
  tenant_id: string | null;
  tenant_name: string;
  network_id: string | null;
  network_name: string;
  assigned_at: string;
  assigned_by: string | null;
  assigned_via: string;
  unassigned_at: string | null;
  unassigned_by: string | null;
  unassigned_via: string | null;
  reference: string | null;
  return_summary: Record<string, number> | null;
}

// Tenant (customer-level) roles; platform_admin is not grantable here.
export const TENANT_ROLES = ['tenant_admin', 'department_admin', 'radio_manager', 'dispatcher', 'read_only', 'booking_manager', 'billing'] as const;

/** Error carrying the server's {code, message, radio_ids|ids}. */
export class ApiError extends Error {
  constructor(message: string, public code?: string, public ids: string[] = []) {
    super(message);
  }
}

/** JSON request with the server error shape mapped to ApiError (shared by the admin API clients). */
export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json', ...(init.headers || {}) } : init.headers,
  });
  if (!response.ok) {
    let body: { error?: string; message?: string; code?: string; radio_ids?: string[]; ids?: string[] } = {};
    try {
      body = await response.json();
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(body.message || body.error || `Request failed (${response.status})`, body.code, body.radio_ids || body.ids || []);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

const request = apiRequest;

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const getTenants = () => request<Tenant[]>('/api/admin/tenants');
export const createTenant = (name: string) => request<{ id: string }>('/api/admin/tenants', json('POST', { name }));
export const renameTenant = (id: string, name: string) => request<void>(`/api/admin/tenants/${id}`, json('PUT', { name }));

/** What permanently deleting a customer does (the server computes it; the delete uses the same definition). */
export interface CustomerDeleteImpact {
  /** Unified people with an assignment in the customer (they lose it); those without other access are disabled. */
  users_unassigned?: { id: string; name: string }[];
  users_disabled?: { id: string; name: string }[];
  customer: { id: string; name: string };
  oem_partner?: { id: string; name: string };
  networks: { id: string; name: string; radios: number }[];
  departments: number;
  groups: number;
  users: { id: string; username: string; login_email?: string; roles: string[]; managed_account: boolean; active_sessions: number }[];
  managed_accounts: number;
  role_assignments: number;
  active_sessions: number;
  /** Radios returned to platform inventory (never deleted). */
  radios_returned: number;
  blocked: boolean;
  block_reason?: string;
  platform_admins?: { id: string; name: string }[];
}
export const previewCustomerDelete = (id: string) => request<CustomerDeleteImpact>(`/api/admin/tenants/${encodeURIComponent(id)}/delete-preview`);
/** Permanently deletes a customer; confirmName must be the customer's exact name. */
export const deleteCustomer = (id: string, confirmName: string) =>
  request<{ status: string; impact: CustomerDeleteImpact }>(`/api/admin/tenants/${encodeURIComponent(id)}`, json('DELETE', { confirm_name: confirmName }));
export const createNetwork = (name: string, tenantId: string) =>
  request<{ id: string }>('/api/admin/organizations', json('POST', { name, tenant_id: tenantId }));
export const moveNetwork = (networkId: string, tenantId: string) =>
  request<void>(`/api/admin/organizations/${networkId}/tenant`, json('PUT', { tenant_id: tenantId }));
/** Network names: trimmed, 1-255 characters (the server validates again). */
export const NETWORK_NAME_MAX = 255;
export function networkNameValid(name: string): boolean {
  const n = name.trim();
  return n !== '' && [...n].length <= NETWORK_NAME_MAX;
}
/** Renames a network (unique platform-wide; 409 NAME_TAKEN). */
export const renameNetwork = (networkId: string, name: string) =>
  request<{ message: string }>(`/api/admin/organizations/${encodeURIComponent(networkId)}`, json('PUT', { name }));

/** What permanently deleting one network does (computed by the server; the delete re-checks it). */
export interface NetworkDeleteImpact {
  network: { id: string; name: string };
  customer: { id: string; name: string };
  /** Returned to the rental inventory (never deleted). */
  radios: { id: string; radio_id: string; name: string }[];
  radios_returned: number;
  groups: { id: string; name: string }[];
  department_scopes: number;
  /** Other network-scoped tables with rows, deleted with the network. */
  data: { table: string; rows: number }[];
  /** Users homed in the network: they block the delete. */
  users: { id: string; name: string }[];
  blocked: boolean;
  block_reason?: string;
  /** Must be sent with the delete; a changed network makes it stale (409 PREVIEW_MISMATCH). */
  fingerprint: string;
}
export const previewNetworkDelete = (networkId: string) =>
  request<NetworkDeleteImpact>(`/api/admin/organizations/${encodeURIComponent(networkId)}/delete-preview`);
/** Permanently deletes a network; confirmName must be its exact name, fingerprint the current preview's. */
export const deleteNetwork = (networkId: string, confirmName: string, fingerprint: string) =>
  request<{ status: string; impact: NetworkDeleteImpact }>(`/api/admin/organizations/${encodeURIComponent(networkId)}`,
    json('DELETE', { confirm_name: confirmName, preview_fingerprint: fingerprint }));

export const getDepartments = (tenantId: string) => request<Department[]>(`/api/admin/tenants/${tenantId}/departments`);
export const createDepartment = (tenantId: string, name: string, description: string) =>
  request<Department>(`/api/admin/tenants/${tenantId}/departments`, json('POST', { name, description }));
export const getDepartment = (id: string) => request<DepartmentDetail>(`/api/admin/departments/${id}`);
export const updateDepartment = (id: string, name: string, description: string) =>
  request<void>(`/api/admin/departments/${id}`, json('PUT', { name, description }));
export const deleteDepartment = (id: string) => request<void>(`/api/admin/departments/${id}`, { method: 'DELETE' });
export const updateDepartmentScopes = (id: string, kind: 'radios' | 'groups', add: string[], remove: string[]) =>
  request<void>(`/api/admin/departments/${id}/${kind}`, json('PUT', { add, remove }));

export const getTenantGroups = (tenantId: string) => request<ScopedResource[]>(`/api/admin/tenants/${tenantId}/groups`);
export const getTenantUsers = (tenantId: string) => request<TenantUser[]>(`/api/admin/tenants/${tenantId}/users`);
export const getRoleAssignments = (tenantId: string) => request<RoleAssignment[]>(`/api/admin/tenants/${tenantId}/role-assignments`);
export const grantRole = (tenantId: string, userId: string, role: string, departmentId: string) =>
  request<{ id: string }>(`/api/admin/tenants/${tenantId}/role-assignments`, json('POST', { user_id: userId, role, department_id: departmentId }));
export const revokeRole = (tenantId: string, assignmentId: string) =>
  request<void>(`/api/admin/tenants/${tenantId}/role-assignments/${assignmentId}`, { method: 'DELETE' });

export const getInventory = (filter: { state?: string; tenant_id?: string; network_id?: string } = {}) => {
  const q = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][]);
  return request<InventoryRadio[]>(`/api/admin/inventory/radios${q.toString() ? `?${q}` : ''}`);
};
export const assignRadios = (radioIds: string[], networkId: string, departmentIds: string[], reference: string) =>
  request<InventoryResult>('/api/admin/inventory/assign', json('POST', { radio_ids: radioIds, network_id: networkId, department_ids: departmentIds, reference }));
export const returnRadios = (radioIds: string[]) =>
  request<InventoryResult>('/api/admin/inventory/return', json('POST', { radio_ids: radioIds }));
export const getRadioHistory = (radioId: string) => request<AssignmentRecord[]>(`/api/admin/inventory/radios/${radioId}/history`);
